import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CheckCircle2, Clock, Radio } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, ok, warn }) {
  const icon = ok ? <CheckCircle2 size={14} className="text-pos" /> : warn ? <AlertTriangle size={14} className="text-warn" /> : <Clock size={14} className="text-slate-500" />;
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-sm text-slate-300">{label}</span>
      </div>
      <div className="font-mono text-xs text-slate-200">{value}</div>
    </div>
  );
}

export default function DataHealth() {
  const [dh, setDh] = useState(null);
  const [worker, setWorker] = useState(null);
  const [live, setLive] = useState(null);
  const [diag, setDiag] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [d, w, l, dg] = await Promise.all([
      api.get("/data-health"),
      api.get("/worker/status"),
      api.get("/live-feed/status"),
      api.get("/live-feed/diagnostic").catch(() => ({ data: null })),
    ]);
    setDh(d.data); setWorker(w.data); setLive(l.data); setDiag(dg.data);
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/live-feed/start");
      toast.success(`Live feed started — ${data.subscribed_tokens} tokens`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    } finally {
      setBusy(false);
    }
  };
  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/live-feed/stop");
      toast("Live feed stopped");
      load();
    } catch (e) {
      toast.error("Stop failed");
    } finally {
      setBusy(false);
    }
  };
  const restart = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/live-feed/restart");
      toast.success(
        `Feed force-restarted — ${data.subscribed_tokens} tokens (fresh token)`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restart failed");
    } finally {
      setBusy(false);
    }
  };

  const fixSubscriptions = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mtf/ensure-subscribed");
      const parts = [];
      if (data.nifty_spot_added) parts.push("NIFTY spot");
      if (data.niftybees_added) parts.push(`NIFTYBEES (${data.niftybees_token})`);
      if (parts.length === 0) {
        toast.success("Subscriptions already correct — nothing to fix");
      } else {
        toast.success(`Added: ${parts.join(", ")}`);
      }
      if (data.error) toast.error(data.error);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fix Subscriptions failed");
    } finally {
      setBusy(false);
    }
  };

  const [kiteTest, setKiteTest] = useState(null);
  const testKiteSession = async () => {
    setBusy(true); setKiteTest(null);
    try {
      const { data } = await api.post("/live-feed/test-kite-session");
      setKiteTest(data);
      if (data.ok) {
        toast.success(`Kite session OK — user ${data.broker_user || data.kite_user_id}`);
      } else {
        toast.error(data.error || "Kite session test failed");
      }
    } catch (e) {
      const errObj = e?.response?.data || { ok: false, error: e.message };
      setKiteTest(errObj);
      toast.error("Test call failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="data-health-page">
      <h1 className="text-2xl font-medium tracking-tight">Data Health</h1>
      <p className="text-xs text-slate-500 uppercase tracking-[0.15em]">
        Stream, worker, instrument freshness &amp; hosting constraint
      </p>

      <div className="surface rounded-sm p-4 border-l-2 border-l-blue-500">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Live Market Data (KiteTicker)</div>
            <div className="text-lg font-mono mt-1 flex items-center gap-2">
              <Radio size={16} className={live?.running ? "text-pos animate-pulse" : "text-slate-500"} />
              {live?.running ? "RUNNING" : "STOPPED"}
            </div>
          </div>
          {!live?.running ? (
            <button
              data-testid="live-feed-start-btn"
              onClick={start}
              disabled={busy}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
            >
              {busy ? "Starting…" : "Start Live Feed"}
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                data-testid="live-feed-fix-subscriptions-btn"
                onClick={fixSubscriptions}
                disabled={busy}
                title="Patch NIFTY spot + NIFTYBEES into the running subscription list without a full restart. Idempotent — safe to click repeatedly."
                className="px-4 py-2 border border-purple-800 text-purple-300 hover:bg-purple-950 text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
              >
                Fix Subscriptions
              </button>
              <button
                data-testid="live-feed-restart-btn"
                onClick={restart}
                disabled={busy}
                title="Tear down & rebuild with the freshest DB access token. Use when the stream looks stale/zombie after morning reconnect."
                className="px-4 py-2 border border-amber-800 text-amber-300 hover:bg-amber-950 text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
              >
                {busy ? "Restarting…" : "Force Restart"}
              </button>
              <button
                data-testid="live-feed-stop-btn"
                onClick={stop}
                disabled={busy}
                className="px-4 py-2 border border-red-800 text-red-400 hover:bg-red-950 text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
              >
                {busy ? "Stopping…" : "Stop Live Feed"}
              </button>
            </div>
          )}
        </div>
        {live?.running && (
          <div className="grid grid-cols-3 gap-4 text-xs font-mono">
            <div><span className="text-slate-500">Tokens: </span>{live.subscribed_tokens}</div>
            <div><span className="text-slate-500">Tick age: </span>{live.tick_age_seconds ? live.tick_age_seconds.toFixed(1) + "s" : "—"}</div>
            <div>
              <span className="text-slate-500">Stale: </span>
              {live.connected_no_ticks
                ? <span className="text-warn">ZOMBIE</span>
                : live.stale
                  ? <span className="text-warn">YES</span>
                  : <span className="text-pos">no</span>}
            </div>
            <div>
              <span className="text-slate-500">Started: </span>
              {live.started_at ? new Date(live.started_at * 1000).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}
            </div>
            <div>
              <span className="text-slate-500">WS connect: </span>
              {live.ws_connected_at ? new Date(live.ws_connected_at * 1000).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" }) : <span className="text-warn">pending</span>}
            </div>
            <div>
              <span className="text-slate-500">First tick: </span>
              {live.first_tick_ts ? new Date(live.first_tick_ts * 1000).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" }) : <span className="text-warn">waiting…</span>}
            </div>
            <div className="col-span-3 text-[10px] text-slate-500 mt-2">
              Futures: {Object.entries(live.futures || {}).map(([k, v]) => `${k}=${v}`).join(", ")}
            </div>
            {live.connected_no_ticks && (
              <div className="col-span-3 mt-2 p-2 rounded-sm bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300">
                <strong>Zombie socket detected:</strong> WebSocket is connected but no ticks have arrived.
                Watchdog will auto-restart within ~90 seconds. If persistent, click Stop then Start.
              </div>
            )}
          </div>
        )}
        {!live?.running && (
          <p className="text-xs text-slate-400 leading-relaxed">
            Requires an active Zerodha connection. Once started, KiteTicker subscribes to the current NIFTY & BANKNIFTY futures plus a ±{12}-strike option universe in FULL mode. Signal evaluation runs on completed 5-minute historical bars every 5 minutes.
          </p>
        )}
      </div>

      {/* Rich diagnostic panel — auto-refreshes every 5s. Surfaces the exact
          watchdog state (tokens match, subscribed count, findings, recent
          events) so a stale-token or empty-subscription bug can be seen
          without SSH. */}
      {diag && (
        <div className="surface rounded-sm p-4 border-l-2 border-l-purple-500" data-testid="live-feed-diagnostic-panel">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
            Live Feed Diagnostic
          </div>
          {diag.diagnosis && diag.diagnosis.length > 0 && (
            <div className="space-y-1 mb-3">
              {diag.diagnosis.map((f, i) => (
                <div key={i} className={`text-xs ${
                  f.level === "ERROR" ? "text-red-400"
                    : f.level === "WARN" ? "text-amber-400"
                    : "text-slate-400"
                }`}>
                  <span className="font-mono text-[10px] mr-2">[{f.level}]</span>
                  {f.message}
                </div>
              ))}
            </div>
          )}
          {diag.running && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] font-mono">
              <div><span className="text-slate-500">Subscribed tokens: </span>{diag.subscribed_tokens_count}</div>
              <div><span className="text-slate-500">Option chain size: </span>{diag.token_to_option_count}</div>
              <div><span className="text-slate-500">Universe: </span>{(diag.active_underlyings || []).join(", ") || "—"}</div>
              <div><span className="text-slate-500">Started age: </span>{diag.started_age_seconds ? `${Math.round(diag.started_age_seconds)}s` : "—"}</div>
              <div><span className="text-slate-500">WS age: </span>{diag.ws_connected_age_seconds ? `${Math.round(diag.ws_connected_age_seconds)}s` : <span className="text-warn">pending</span>}</div>
              <div><span className="text-slate-500">First tick age: </span>{diag.first_tick_age_seconds ? `${Math.round(diag.first_tick_age_seconds)}s` : <span className="text-warn">none</span>}</div>
              <div><span className="text-slate-500">Live token hash: </span>{diag.live_access_token_hash || "—"}</div>
              <div><span className="text-slate-500">DB token hash: </span>{diag.db_access_token_plain_hash || diag.db_access_token_hash || "—"}</div>
              <div>
                <span className="text-slate-500">Tokens match: </span>
                {diag.tokens_match === true
                  ? <span className="text-pos">yes</span>
                  : diag.tokens_match === false
                    ? <span className="text-red-400">NO — click &quot;Test Kite Session&quot; below</span>
                    : <span className="text-slate-500">—</span>}
              </div>
              <div className="col-span-2 md:col-span-3"><span className="text-slate-500">Selected expiries: </span>{Object.entries(diag.selected_expiries || {}).map(([k, v]) => `${k}=${v}`).join(", ") || "—"}</div>
            </div>
          )}
          {/* Test Kite Session — the single most reliable diagnosis
              tool. Fires kite.profile() over REST (bypasses WebSocket
              entirely). If this succeeds, the token is fine and the
              zombie is a gateway/network issue. If it fails, Zerodha's
              daily token rolled over — click Reconnect on the Dashboard. */}
          <div className="mt-4 border-t border-slate-800 pt-3 flex items-center gap-2 flex-wrap"
               data-testid="test-kite-session-row">
            <button onClick={testKiteSession} disabled={busy}
                    data-testid="test-kite-session-btn"
                    className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm bg-slate-800 hover:bg-purple-700 disabled:opacity-40 text-slate-100">
              Test Kite Session
            </button>
            <span className="text-[10px] text-slate-500">
              Runs <code>kite.profile()</code> — proves whether the stored access_token is still accepted by Zerodha.
            </span>
          </div>
          {kiteTest && (
            <div className={`mt-2 p-3 rounded-sm text-xs font-mono ${kiteTest.ok ? "bg-emerald-900/20 border border-emerald-700 text-pos" : "bg-red-900/20 border border-red-700 text-red-300"}`}
                 data-testid="test-kite-session-result">
              {kiteTest.ok ? (
                <div>
                  <div className="font-semibold">OK — token is valid</div>
                  <div className="text-slate-300 mt-1">
                    broker_user: {kiteTest.broker_user || "—"} · kite_user_id: {kiteTest.kite_user_id || "—"}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold">FAILED — {kiteTest.error}</div>
                  {kiteTest.hint ? (
                    <div className="text-slate-300 mt-1">{kiteTest.hint}</div>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {diag.recent_events && diag.recent_events.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                Recent watchdog events (newest first)
              </div>
              <div className="space-y-0.5 text-[10px] font-mono max-h-64 overflow-y-auto">
                {diag.recent_events.map((e, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-slate-600 shrink-0">
                      {new Date(e.timestamp).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}
                    </span>
                    <span className={
                      e.level === "ERROR" ? "text-red-400 shrink-0"
                        : e.level === "WARN" ? "text-amber-400 shrink-0"
                        : "text-slate-500 shrink-0"
                    }>[{e.level || "INFO"}]</span>
                    <span className="text-slate-300 shrink-0">{e.message}</span>
                    {e.detail && (
                      <span className="text-slate-500 truncate">
                        {typeof e.detail === "object"
                          ? JSON.stringify(e.detail)
                          : String(e.detail)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="surface rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Feed</div>
          <Row label="Stream state" value={dh?.stream_state || "—"} ok={dh?.stream_state === "LIVE"} warn={dh?.stream_state === "STALE" || dh?.stream_state === "DEMO"} />
          <Row label="Last tick (IST)" value={dh?.last_tick_ist ? new Date(dh.last_tick_ist).toLocaleTimeString("en-IN") : "—"} />
          <Row label="Tick age (sec)" value={dh?.tick_age_seconds !== null && dh?.tick_age_seconds !== undefined ? dh.tick_age_seconds.toFixed(1) : "—"} />
          <Row label="Instrument snapshot" value={dh?.instrument_snapshot_date || "—"} ok={!!dh?.instrument_snapshot_date} />
          <Row label="Completed candle OK" value={dh?.completed_candle_ok ? "yes" : "no"} ok={dh?.completed_candle_ok} />
          <Row label="Holiday calendar" value={dh?.holiday_calendar_updated_at || "—"} />
          {dh?.stale_reasons?.length > 0 && (
            <div className="mt-3 p-3 rounded-sm bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300">
              {dh.stale_reasons.map((r) => <div key={r}>• {r}</div>)}
            </div>
          )}
        </div>

        <div className="surface rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Market Worker</div>
          <Row label="Holder" value={worker?.holder || "—"} />
          <Row label="Fencing token" value={worker?.fencing_token ?? "—"} />
          <Row label="Last heartbeat" value={worker?.last_heartbeat ? new Date(worker.last_heartbeat).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"} ok={!worker?.stale} warn={worker?.stale} />
          <Row label="Stale?" value={worker?.stale ? "STALE" : "healthy"} ok={!worker?.stale} warn={worker?.stale} />
          <Row label="Always-on verified" value={worker?.verified_always_on ? "yes" : "NO"} warn={!worker?.verified_always_on} />

          <div className="mt-3 p-3 rounded-sm bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200 leading-relaxed">
            <strong>Hosting notice:</strong> {worker?.note}
          </div>
        </div>
      </div>
    </div>
  );
}
